import { useEffect, useState, useRef } from "react";
import chatSocketService from "../../../services/websocket";

const MESSAGES_PER_PAGE = 20;

export const useChatMessages = (venueUUID, hasCheckedIn) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const messagesRef = useRef([]);
  const unsubscribeRef = useRef(null);

  // Mesajları birleştir ve sırala
  const mergeAndSortMessages = (newMessages, existingMessages = []) => {
    const messageMap = new Map();

    // Mevcut mesajları map'e ekle
    existingMessages.forEach(msg => {
      if (msg.id) {
        messageMap.set(msg.id, msg);
      }
    });

    // Yeni mesajları map'e ekle (üzerine yaz)
    newMessages.forEach(msg => {
      if (msg.id) {
        messageMap.set(msg.id, msg);
      }
    });

    // Map'i array'e çevir ve timestamp'e göre sırala
    const sortedMessages = Array.from(messageMap.values()).sort((a, b) => {
      const timestampA = a.timestamp || 0;
      const timestampB = b.timestamp || 0;
      return timestampA - timestampB;
    });

    return sortedMessages;
  };

  // Yeni mesaj geldiğinde çağrılacak fonksiyon
  const handleNewMessage = (message) => {
    setMessages(prevMessages => {
      // Duplicate kontrolü - aynı ID'li mesaj zaten varsa eklememe
      const existingMessage = prevMessages.find(msg => msg.id === message.id);
      if (existingMessage) {
        console.log('🚫 Duplicate mesaj engellendi:', message.id);
        return prevMessages; // Değişiklik yapma
      }

      const updatedMessages = mergeAndSortMessages([message], prevMessages);
      messagesRef.current = updatedMessages;
      return updatedMessages;
    });
  };

  // Venue'ye katıl ve eski mesajları al
  const joinVenueAndLoadMessages = async (venueId) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('🏢 Venue\'ye katılıyor ve mesajlar yükleniyor:', venueId);

      // Venue'ye katıl ve eski mesajları al
      const initialMessages = await chatSocketService.joinVenue(venueId);
      console.log('📦 JoinVenue\'den dönen mesajlar:', initialMessages);
      console.log('📊 Dönen mesaj sayısı:', initialMessages?.length || 0);
      
      const sortedMessages = mergeAndSortMessages(initialMessages, []);
      console.log('🔄 Merge sonrası mesajlar:', sortedMessages);
      
      setMessages(sortedMessages);
      messagesRef.current = sortedMessages;
      
      // İlk yüklemede sayfa sayısını sıfırla
      setCurrentPage(0);
      setHasMoreMessages(initialMessages.length >= MESSAGES_PER_PAGE);

      console.log('✅ Mesajlar state\'e set edildi, toplam:', sortedMessages.length);

    } catch (error) {
      console.error("❌ Venue join hatası:", error);
      setError("Mekana bağlanırken hata oluştu: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Daha fazla mesaj yükle (infinite scroll)
  const loadMoreMessages = async () => {
    if (!venueUUID || !hasCheckedIn || isLoadingMore || !hasMoreMessages) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const nextPage = currentPage + 1;
      
      // Socket bağlantısını kontrol et
      if (!chatSocketService.connected) {
        console.warn("Socket bağlantısı yok, eski mesajlar yüklenemedi");
        setHasMoreMessages(false);
        return;
      }
      
      // Socket'e daha fazla mesaj iste
      const olderMessages = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.warn("Eski mesaj yükleme timeout'u");
          chatSocketService.socket.off('older_messages', handleOlderMessages);
          // Timeout durumunda error fırlatmak yerine boş array döndür
          resolve([]);
        }, 20000); // Timeout'u 20 saniyeye çıkardık

        const handleOlderMessages = (data) => {
          clearTimeout(timeout);
          chatSocketService.socket.off('older_messages', handleOlderMessages);
          console.log('Eski mesajlar alındı:', data);
          resolve(data.messages || []);
        };

        // Önce listener'ı ekle, sonra emit yap
        chatSocketService.socket.on('older_messages', handleOlderMessages);
        
        chatSocketService.socket.emit('load_more_messages', {
          venueId: venueUUID,
          page: nextPage,
          limit: MESSAGES_PER_PAGE
        });

        console.log('Eski mesajlar istendi:', { venueId: venueUUID, page: nextPage, limit: MESSAGES_PER_PAGE });
      });

      if (olderMessages.length > 0) {
        const updatedMessages = mergeAndSortMessages(olderMessages, messagesRef.current);
        setMessages(updatedMessages);
        messagesRef.current = updatedMessages;
        setCurrentPage(nextPage);
        setHasMoreMessages(olderMessages.length >= MESSAGES_PER_PAGE);
        console.log(`${olderMessages.length} eski mesaj yüklendi`);
      } else {
        setHasMoreMessages(false);
        console.log('Daha fazla eski mesaj yok');
      }

    } catch (error) {
      console.error("Eski mesajlar yüklenirken hata:", error);
      // Error'ı state'e set etmek yerine sadece log'la ve hasMoreMessages'ı false yap
      setHasMoreMessages(false);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!venueUUID || !hasCheckedIn) {
      setMessages([]);
      setIsLoading(false);
      setError(null);
      setHasMoreMessages(true);
      setCurrentPage(0);
      messagesRef.current = [];
      
      // Mevcut listener'ı temizle
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      return;
    }

    console.log("🚀 useChatMessages başlatılıyor:", { venueUUID, hasCheckedIn });

    // Socket bağlantısını kontrol et - retry mekanizması ile
    const checkSocketAndLoadMessages = async () => {
      let retryCount = 0;
      const maxRetries = 15; // 15 saniye bekle (socketService.js'deki gibi)
      
      while (retryCount < maxRetries) {
        if (chatSocketService.connected) {
          console.log("✅ Socket bağlantısı mevcut, mesajlar yükleniyor...");
          // Venue'ye katıl ve mesajları yükle
          await joinVenueAndLoadMessages(venueUUID);
          
          // Yeni mesajları dinle
          unsubscribeRef.current = chatSocketService.addMessageListener(handleNewMessage);
          return;
        }
        
        console.log(`⏳ Socket bağlantısı bekleniyor... (${retryCount + 1}/${maxRetries})`);
        retryCount++;
        
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // Socket bağlantısı kurulamadı
      console.error("❌ Socket bağlantısı kurulamadı - maksimum deneme sayısına ulaşıldı");
      setError("Bağlantı yok. Lütfen sayfayı yenileyin.");
      setIsLoading(false);
    };

    checkSocketAndLoadMessages();

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [venueUUID, hasCheckedIn]);

  return {
    messages,
    isLoading,
    error,
    isLoadingMore,
    hasMoreMessages,
    loadMoreMessages,
    setMessages,
  };
};
